import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { topicCompletionUpdated } from '../../features/topic-completion/events';
import type { UpNextState, Topic } from './types';

const initialState: UpNextState = {
  isLoading: false,
  hasLoaded: false,
  isDraftLoading: false,
  error: false,
  upNextTopics: [],
  topicsPagination: {
    total: 0,
    page: 1,
    pageSize: 20,
    draftTopics: [],
    hasMore: true
  }
};

export const slice = createSlice({
  name: 'upNext',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(topicCompletionUpdated, (state, action) => {
      state.queueRequestId = null;
      state.draftRequestId = null;
      if (action.payload.published) {
        state.upNextTopics = state.upNextTopics.filter((t) => t.topicGuid !== action.payload.guid);
        const before = state.topicsPagination.draftTopics.length;
        state.topicsPagination.draftTopics = state.topicsPagination.draftTopics.filter(
          (t) => t.topicGuid !== action.payload.guid
        );
        state.topicsPagination.total = Math.max(
          0,
          state.topicsPagination.total - (before - state.topicsPagination.draftTopics.length)
        );
      }
    });
  },
  reducers: {
    startLoading(state, action: PayloadAction<void> & { meta?: { requestId: string } }) {
      state.queueRequestId = action.meta?.requestId ?? null;
      state.isLoading = true;
    },
    stopLoading(state, action: PayloadAction<void> & { meta?: { requestId: string } }) {
      if (action.meta && state.queueRequestId !== action.meta.requestId) return;
      state.isLoading = false;
    },
    startDraftLoading(state, action: PayloadAction<void> & { meta?: { requestId: string } }) {
      state.draftRequestId = action.meta?.requestId ?? null;
      state.isDraftLoading = true;
    },
    hasError(state, action: PayloadAction<boolean> & { meta?: { requestId: string } }) {
      if (action.meta && state.queueRequestId !== action.meta.requestId) return;
      state.error = action.payload;
      state.isLoading = false;
      state.isDraftLoading = false;
      state.hasLoaded = true;
    },
    fetchUpNextTopicsSuccess(
      state,
      action: PayloadAction<Topic[]> & { meta?: { requestId: string } }
    ) {
      if (action.meta && state.queueRequestId !== action.meta.requestId) return;
      state.upNextTopics = action.payload.filter((topic: Topic) => !topic.topicCompleted);
      state.hasLoaded = true;
    },
    fetchDraftTopicsSuccess(
      state,
      action: PayloadAction<{
        data: Topic[];
        pagination: { page: number; pageSize: number; total: number };
      }> & { meta?: { requestId: string } }
    ) {
      if (action.meta && state.draftRequestId !== action.meta.requestId) return;
      const newTopics = action.payload.data.filter(
        (topic: Topic) =>
          !topic.topicPublished &&
          !state.upNextTopics.some((ft) => ft.topicGuid === topic.topicGuid)
      );

      const isFirstPage = action.payload.pagination.page === 1;
      state.topicsPagination = {
        ...action.payload.pagination,
        draftTopics: isFirstPage
          ? newTopics
          : [...state.topicsPagination.draftTopics, ...newTopics],
        hasMore:
          action.payload.pagination.page * action.payload.pagination.pageSize <
          action.payload.pagination.total
      };
      state.isDraftLoading = false;
    },
    draftRequestFailed(state, action: PayloadAction<string>) {
      if (state.draftRequestId !== action.payload) return;
      state.isDraftLoading = false;
      state.error = true;
    },
    clearDraftTopics(state) {
      state.draftRequestId = null;
      state.topicsPagination.draftTopics = [];
      state.topicsPagination.page = 1;
      state.topicsPagination.hasMore = true;
      state.isDraftLoading = false;
    },
    removeUpNextTopic(state, action) {
      state.upNextTopics = state.upNextTopics.filter(
        (topic) => topic.topicGuid !== action.payload.topicGuid
      );
      if (!action.payload.topicCompleted) {
        state.topicsPagination.draftTopics = [
          action.payload,
          ...state.topicsPagination.draftTopics
        ];
      }
    },
    addUpNextTopic(state, action) {
      state.upNextTopics.push(action.payload);
      state.topicsPagination.draftTopics = state.topicsPagination.draftTopics.filter(
        (topic) => topic.topicGuid !== action.payload.topicGuid
      );
    }
  }
});

export default slice.reducer;
